import { createColumnHelper } from "@tanstack/react-table";
import classNames from "classnames";
import moment from "moment-timezone";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ResourceSelect from "../../../components/Inputs/ResourceSelect";
import Select from "../../../components/Inputs/Select";
import { dateTimeFormat } from "../../../utils/helpers/date";
import {
	getSettingsOrgResourceSearchFilters,
	getSettingsResourceSearchFilters,
	renderOrgLevelResourceLabel,
	renderSettingsResourceLabel,
} from "../../../utils/helpers/settings";
import { renderUserName } from "../../../utils/helpers/user";
import StatusCell from "../Home/StatusCell";
import { OrgLevelGroupsContext } from "../../Settings/General/OrganisationLevelGroups/context";
import { useCompanyDateFormat, useCompanyTimeFormat } from "../../../utils/hooks/company";
import { useUser } from "../../../utils/hooks/user";
import { canRequest } from "../permissions";

const columnHelper = createColumnHelper();

export const useColumns = ({ approve, deny, view, namespace, accessMap }) => {
	const { t } = useTranslation();
	const { groups } = useContext(OrgLevelGroupsContext);
	const dateFormat = useCompanyDateFormat();
	const timeFormat = useCompanyTimeFormat();

	const { Option } = Select;
	const user = useUser();

	const columns = useMemo(() => {
		const cols = [
			columnHelper.accessor("user.employeeNumber", {
				enableColumnFilter: true,
				header: t("employee-number"),
			}),
			columnHelper.accessor("user", {
				enableColumnFilter: true,
				header: t("employee-name"),
				cell: (info) => {
					const value = info.getValue();
					return value && renderUserName(value);
				},
			}),
		];

		if (namespace === "timesheet") {
			cols.push(
				columnHelper.accessor("date", {
					header: t("date"),
					cell: ({ row: { original } }) => {
						const date = original?.data?.date;
						return date ? moment(date).format(dateFormat) : null;
					}
				}),
			);
		}

		if (!["availability", "timesheet"].includes(namespace)) {
			cols.splice(3, 0, columnHelper.accessor("startDate", {
				enableColumnFilter: true,
				header: t("start-date"),
				cell: (info) => {
					const { data } = info.row.original;
					return dateTimeFormat(data?.startDate, dateFormat);
				},
			}),
				columnHelper.accessor("startTime", {
					header: t("start-time"),
					cell: (info) => {
						const { data } = info.row.original;
						return dateTimeFormat(data?.startDate, timeFormat);
					},
				}),
				columnHelper.accessor("endDate", {
					header: t("end-date"),
					cell: (info) => {
						const { data } = info.row.original;
						return dateTimeFormat(data?.endDate, dateFormat);
					},
				}),
				columnHelper.accessor("endTime", {
					header: t("end-time"),
					cell: (info) => {
						const { data } = info.row.original;
						return dateTimeFormat(data?.endDate, timeFormat);
					},
				}));
		}

		if (["clock", "schedule", "availability", "timesheet"].includes(namespace)) {
			cols.push(columnHelper.accessor("type", {
				header: t("type"),
				cell: (info) => t(info.getValue())
			}));
		}


		if (namespace === "pto") {
			cols.push(columnHelper.accessor("plan", {
				enableColumnFilter: true,
				header: t("plan"),
				cell: ({ row: { original } }) => {
					const plan = original?.data?.plan?.plan;
					return plan && renderSettingsResourceLabel(plan)
				},
				filterType: "resource-select",
				Filter: (
					<ResourceSelect
						mode="multiple"
						labelPropName="description"
						resourcePath="/timeoff"
						renderLabel={renderSettingsResourceLabel}
						hasSearch
						getSearchFilters={getSettingsResourceSearchFilters}
					/>
				)
			}));
		}

		if (namespace === "clock") {
			cols.push(
				columnHelper.accessor("data.location", {
					enableColumnFilter: true,
					header: t("location"),
					cell: (info) => {
						const value = info.getValue();
						return value && renderSettingsResourceLabel(value);
					},
					filterType: "resource-select",
					Filter: (
						<ResourceSelect
							labelPropName="description"
							resourcePath="/locations"
							mode="multiple"
							renderLabel={renderSettingsResourceLabel}
							getSearchFilters={getSettingsResourceSearchFilters}
							hasSearch
						/>
					),
				}),
				columnHelper.accessor("data.job", {
					enableColumnFilter: true,
					header: t("job"),
					cell: (info) => {
						const value = info.getValue();
						return value && renderSettingsResourceLabel(value);
					},
					filterType: "resource-select",
					Filter: (
						<ResourceSelect
							labelPropName="description"
							resourcePath="/jobs"
							mode="multiple"
							renderLabel={renderSettingsResourceLabel}
							getSearchFilters={getSettingsResourceSearchFilters}
							hasSearch
						/>
					),
				})
			);
		}

		if (groups) {
			const groupsColumns = groups.map((group) => {
				const level = group.level.replace("_", "");
				return columnHelper.accessor(level, {
					enableColumnFilter: true,
					header: group.description,
					cell: (info) => {
						const value = info.getValue();
						return value && renderOrgLevelResourceLabel(value);
					},
					filterType: "select",
					Filter: (
						<ResourceSelect
							labelPropName="description"
							resourcePath={`/${group?.id}/org-levels`}
							mode="multiple"
							renderLabel={renderOrgLevelResourceLabel}
							getSearchFilters={
								getSettingsOrgResourceSearchFilters
							}
							valuePropName="id"
							hasSearch
						/>
					),
				});
			});
			cols.push(...groupsColumns);
		}

		cols.push(
			columnHelper.accessor("status", {
				enableColumnFilter: true,
				header: t("status"),
				cell: ({ row }) => <StatusCell value={row.original} />,
				filterType: "select",
				Filter: (
					<Select>
						<Option value="approved">{t("approved")}</Option>
						<Option value="denied">{t("denied")}</Option>
						<Option value="pending">{t("pending")}</Option>
						<Option value="canceled">{t("canceled")}</Option>
						<Option value="expired">{t("expired")}</Option>
					</Select>
				),
			})
		);

		// if (approve || deny || view) {
		const actionsColumn = columnHelper.display({
			id: "actions",
			enableHiding: false,
			enableColumnFilter: false,
			header: () => (
				<div className="text-right">
					{t("actions")}
				</div>
			),
			cell: (info) => {
				const { id, status, requiredToBeApprovedBy } = info.row.original;
				const noop = () => {};
				const canViewRow = canRequest(accessMap, { namespace, type: info.row.original?.type, action: "canView", team: true });
				const canApproveRow = canRequest(accessMap, { namespace, type: info.row.original?.type, action: "canApprove", team: true }) || (requiredToBeApprovedBy || []).includes(user.id);
				const canDenyRow = canRequest(accessMap, { namespace, type: info.row.original?.type, action: "canDeny", team: true }) || (requiredToBeApprovedBy || []).includes(user.id);
				const approveAction = canApproveRow ? () => approve?.({ id, close: noop }) : null;
				const denyAction = canDenyRow ? () => deny?.({ id, close: noop }) : null;
				const viewAction = canViewRow ? () => view?.(info.row.original) : null;
				return (
					<div
						className={classNames(
							"d-flex align-items-center justify-content-end gap-x-2",
						)}
					>
						{approveAction &&
							(status === "pending" || status === "pending-canceled") && (
							<i
								className="fa-solid fa-check text-green cursor-pointer"
								onClick={approveAction}
								style={{ fontSize: "17px" }}
							/>
						)}
						{denyAction &&
							(status === "pending" || status === "pending-canceled") && (
							<i
								className="fa-solid fa-xmark text-danger cursor-pointer mx-2"
								onClick={denyAction}
								style={{ fontSize: "17px" }}
							/>
						)}
						{viewAction && (
							<i
								className="fa-solid fa-eye text-dark cursor-pointer"
								onClick={viewAction}
								style={{ fontSize: "17px" }}
							/>
						)}
					</div>
				);
			},
		});
		cols.push(actionsColumn);
		// }

		return cols;
	}, [t, namespace, groups, dateFormat, timeFormat, user.id, view, approve, deny, accessMap]);

	return columns;
};
